import React from "react";
import { AlertCircle, Banknote, CheckCircle, CreditCard, ExternalLink, HelpCircle, Landmark, QrCode, Receipt, UploadCloud, Users } from "lucide-react";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";

export default function FinancialWaterfallWidget({ waterfall, onOpenAggregatorModal }) {
  if (!waterfall) return null;

  const bookedSales = waterfall.booked_invoiced_sales || 0;
  const liquidCollections = waterfall.liquid_collections || 0;
  const aggregatorGross = waterfall.aggregator_sales_gross || 0;
  const khataCredit = waterfall.khata_credit_extended || 0;
  const unsettled = waterfall.unsettled_printed_bills || 0;
  const delta = waterfall.reconciliation_delta || 0;

  const tender = waterfall.tender_breakdown || {};
  const aggregators = waterfall.aggregators || {};
  const khata = waterfall.khata || {};

  return (
    <section className="rounded-lg border bg-card p-loose space-y-base" data-testid="dash-financial-waterfall">
      <div className="flex flex-wrap items-center justify-between gap-snug border-b pb-snug">
        <div>
          <h2 className="text-lede font-semibold">Financial Waterfall & Tender Allocation</h2>
          <p className="text-meta text-muted-foreground">
            Authoritative reconciliation of Booked Sales against Liquid Collections, Aggregators, Credit & Unsettled Bills
          </p>
        </div>
        <div className="flex items-center gap-snug">
          {delta === 0 ? (
            <span className="inline-flex items-center gap-tight rounded bg-success-subtle px-snug py-hair text-meta font-medium text-success">
              <CheckCircle className="h-3.5 w-3.5" />
              <span>Tenders Reconciled (Δ = ₹0.00)</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-tight rounded bg-danger-subtle px-snug py-hair text-meta font-medium text-danger">
              <AlertCircle className="h-3.5 w-3.5" />
              <span>Reconciliation Delta: {inr(delta)}</span>
            </span>
          )}
        </div>
      </div>

      {/* Waterfall Formula Bar */}
      <div className="rounded-md border bg-muted/40 p-base text-meta">
        <div className="flex flex-wrap items-center gap-base font-medium">
          <div className="flex items-center gap-tight">
            <span className="text-muted-foreground">Booked Invoiced Sales:</span>
            <span className="text-body font-bold tabular-nums text-foreground">{inr(bookedSales)}</span>
          </div>
          <span className="text-muted-foreground">=</span>
          <div className="flex items-center gap-tight">
            <span className="text-success">Liquid Collections:</span>
            <span className="font-semibold tabular-nums text-foreground">{inr(liquidCollections)}</span>
          </div>
          <span className="text-muted-foreground">+</span>
          <div className="flex items-center gap-tight">
            <span className="text-primary">Aggregator Receivables:</span>
            <span className="font-semibold tabular-nums text-foreground">{inr(aggregatorGross)}</span>
          </div>
          <span className="text-muted-foreground">+</span>
          <div className="flex items-center gap-tight">
            <span className="text-amber-600 dark:text-amber-400">Khata Credit:</span>
            <span className="font-semibold tabular-nums text-foreground">{inr(khataCredit)}</span>
          </div>
          <span className="text-muted-foreground">+</span>
          <div className="flex items-center gap-tight">
            <span className="text-muted-foreground">Unsettled:</span>
            <span className="font-semibold tabular-nums text-foreground">{inr(unsettled)}</span>
          </div>
        </div>
      </div>

      {/* Four Component Breakdown Cards */}
      <div className="grid gap-base md:grid-cols-2 lg:grid-cols-4">
        {/* 1. Liquid Collections */}
        <div className="rounded-md border p-base space-y-tight">
          <div className="flex items-center justify-between text-meta font-medium text-success">
            <span className="flex items-center gap-tight">
              <Banknote className="h-4 w-4" />
              <span>Liquid Collections</span>
            </span>
            <span className="font-bold tabular-nums">{inr(liquidCollections)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Funds physically verified in register drawer or direct bank account
          </p>
          <div className="space-y-hair border-t pt-tight text-[11px] text-muted-foreground">
            <div className="flex justify-between">
              <span>Cash Drawer:</span>
              <span className="font-medium text-foreground tabular-nums">{inr(tender.cash || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>UPI / QR:</span>
              <span className="font-medium text-foreground tabular-nums">{inr(tender.upi || 0)}</span>
            </div>
            <div className="flex justify-between">
              <span>Card / POS Machine:</span>
              <span className="font-medium text-foreground tabular-nums">{inr(tender.card || 0)}</span>
            </div>
          </div>
        </div>

        {/* 2. Aggregator Receivables */}
        <div className="rounded-md border p-base space-y-tight">
          <div className="flex items-center justify-between text-meta font-medium text-primary">
            <span className="flex items-center gap-tight">
              <Landmark className="h-4 w-4" />
              <span>Aggregator Receivables</span>
            </span>
            <span className="font-bold tabular-nums">{inr(aggregatorGross)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Billed to online portals. Payout pending bank settlement
          </p>
          <div className="space-y-hair border-t pt-tight text-[11px] text-muted-foreground">
            <div className="flex justify-between">
              <span>Orders:</span>
              <span className="font-medium text-foreground tabular-nums">{aggregators.orders_count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Est. Commission:</span>
              <span className="font-medium text-danger tabular-nums">-{inr(aggregators.estimated_commission || 0)}</span>
            </div>
            <div className="flex justify-between font-medium">
              <span>Est. Net Payout:</span>
              <span className="text-foreground tabular-nums">{inr(aggregators.estimated_net_payout || 0)}</span>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="w-full h-7 text-[11px] gap-tight mt-tight"
            onClick={onOpenAggregatorModal}
          >
            <UploadCloud className="h-3 w-3" />
            <span>Reconcile CSV</span>
          </Button>
        </div>

        {/* 3. Khata Credit Extended */}
        <div className="rounded-md border p-base space-y-tight">
          <div className="flex items-center justify-between text-meta font-medium text-amber-600 dark:text-amber-400">
            <span className="flex items-center gap-tight">
              <Users className="h-4 w-4" />
              <span>Khata Credit Extended</span>
            </span>
            <span className="font-bold tabular-nums">{inr(khataCredit)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Billed orders added to customer debt ledgers today
          </p>
          <div className="space-y-hair border-t pt-tight text-[11px] text-muted-foreground">
            <div className="flex justify-between">
              <span>Credit Orders:</span>
              <span className="font-medium text-foreground tabular-nums">{khata.orders_count || 0}</span>
            </div>
            <div className="flex justify-between">
              <span>Debt Recoveries:</span>
              <span className="font-medium text-success tabular-nums">+{inr(khata.cash_recoveries || 0)}</span>
            </div>
            <p className="text-[10px] text-muted-foreground italic pt-hair">
              *Debt repayments enter cash drawer directly without double-counting sales
            </p>
          </div>
        </div>

        {/* 4. Unsettled Printed Bills */}
        <div className="rounded-md border p-base space-y-tight">
          <div className="flex items-center justify-between text-meta font-medium text-muted-foreground">
            <span className="flex items-center gap-tight">
              <Receipt className="h-4 w-4" />
              <span>Printed Bills (Billing)</span>
            </span>
            <span className="font-bold tabular-nums text-foreground">{inr(unsettled)}</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Printed slips with customers, awaiting cashier tender settlement
          </p>
          <div className="space-y-hair border-t pt-tight text-[11px] text-muted-foreground">
            <div className="flex justify-between">
              <span>Awaiting Payment:</span>
              <span className="font-medium text-foreground tabular-nums">
                {waterfall.unsettled_bills_count || 0} bills
              </span>
            </div>
            <div className="flex justify-between">
              <span>Average Ticket:</span>
              <span className="font-medium text-foreground tabular-nums">
                {waterfall.unsettled_bills_count > 0
                  ? inr(unsettled / waterfall.unsettled_bills_count)
                  : inr(0)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
